import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import {
  SectionAdminBar,
  useBlockConfig,
  AdminCTAButton,
} from "./shared";

interface HeroSectionBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function HeroSectionBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: HeroSectionBlockProps) {
  const { get, setConfig, d } = useBlockConfig(block, onUpdate);
  const bgImage = get("bgImage", "");
  const headline1 = get("headline1", "");
  const headlineAccent = get("headlineAccent", "");
  const tagline = get("tagline", "");
  const body = get("body", "");
  const ctaPrimary = get("ctaPrimary", "Apply Now");
  const ctaSecondary = get("ctaSecondary", "View Spaces");
  const ctaPrimaryUrl = get("ctaPrimaryUrl", "#application");
  const ctaSecondaryUrl = get("ctaSecondaryUrl", "#spaces");
  const stats = get("stats", []) as { value: string; label: string }[];

  const setStat = (idx: number, key: "value" | "label", val: string) => {
    const newStats = stats.map((s, i) => (i === idx ? { ...s, [key]: val } : s));
    setConfig("stats", newStats);
  };

  const addStat = () => {
    setConfig("stats", [...stats, { value: "100%", label: "New Metric" }]);
  };

  const deleteStat = (idx: number) => {
    setConfig("stats", stats.filter((_, i) => i !== idx));
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="hero"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="relative w-full min-h-[520px] flex items-center justify-center overflow-hidden rounded-2xl">
        <div className="absolute inset-0 w-full h-full">
          {isAdmin && onUpdate ? (
            <AdminImageUpload
              src={bgImage || ""}
              alt="Hero background"
              onReplace={(url) => setConfig("bgImage", url)}
              className="w-full h-full"
              imgClassName="w-full h-full object-cover object-top"
              objectPosition={(d as Record<string, unknown>).bgPosition as string ?? "50% 20%"}
              onPositionChange={(pos) => setConfig("bgPosition", pos)}
              isDark={isDark}
            />
          ) : bgImage ? (
            <img src={bgImage} alt="Hero background" className="w-full h-full object-cover object-top" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-6 py-16 max-w-3xl mx-auto w-full">
          {/* Headline */}
          {isAdmin && onUpdate ? (
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4" style={{ fontFamily: headingFont, ...headingStyle }}>
              <AdminEditField
                value={headline1}
                onSave={(v) => setConfig("headline1", v)}
                inputClassName="text-center text-4xl md:text-6xl font-extrabold"
              >
                <span>{headline1}</span>
              </AdminEditField>{" "}
              <AdminEditField
                value={headlineAccent}
                onSave={(v) => setConfig("headlineAccent", v)}
                inputClassName="text-center text-4xl md:text-6xl font-extrabold text-green-400"
              >
                <span className="text-green-400">{headlineAccent}</span>
              </AdminEditField>
            </h1>
          ) : (
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4" style={{ fontFamily: headingFont, ...headingStyle }}>
              {headline1}{" "}<span className="text-green-400">{headlineAccent}</span>
            </h1>
          )}

          {/* Tagline */}
          {tagline && (
            <AdminEditField
              value={tagline}
              onSave={(v) => setConfig("tagline", v)}
              inputClassName="text-center text-xl"
            >
              <p className="text-xl text-white/90 font-semibold mb-4">{tagline}</p>
            </AdminEditField>
          )}

          {/* Body */}
          {body && (
            <AdminEditField
              value={body}
              onSave={(v) => setConfig("body", v)}
              multiline
              inputClassName="text-center"
            >
              <p className="text-base text-white/80 mb-8 max-w-xl mx-auto">{body}</p>
            </AdminEditField>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <div className="flex flex-col items-center gap-1.5">
              {isAdmin && onUpdate ? (
                <>
                  <AdminEditField
                    value={ctaPrimary}
                    onSave={(v) => setConfig("ctaPrimary", v)}
                    inputClassName="text-center font-bold"
                  >
                    <AdminCTAButton variant="primary">{ctaPrimary}</AdminCTAButton>
                  </AdminEditField>
                  <AdminEditField
                    value={ctaPrimaryUrl}
                    onSave={(v) => setConfig("ctaPrimaryUrl", v)}
                    inputClassName="text-xs text-center text-white/60"
                  >
                    <span className="text-xs text-white/60">URL: {ctaPrimaryUrl || "#application"}</span>
                  </AdminEditField>
                </>
              ) : (
                <button
                  onClick={() => { const url = ctaPrimaryUrl || "#application"; if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); } else if (url.startsWith("#")) { const el = document.querySelector(url); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = `/${url}`; } } else { window.location.href = url; } }}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
                >
                  {ctaPrimary}
                </button>
              )}
            </div>
            <div className="flex flex-col items-center gap-1.5">
              {isAdmin && onUpdate ? (
                <>
                  <AdminEditField
                    value={ctaSecondary}
                    onSave={(v) => setConfig("ctaSecondary", v)}
                    inputClassName="text-center font-bold"
                  >
                    <AdminCTAButton variant="secondary">{ctaSecondary}</AdminCTAButton>
                  </AdminEditField>
                  <AdminEditField
                    value={ctaSecondaryUrl}
                    onSave={(v) => setConfig("ctaSecondaryUrl", v)}
                    inputClassName="text-xs text-center text-white/60"
                  >
                    <span className="text-xs text-white/60">URL: {ctaSecondaryUrl || "#spaces"}</span>
                  </AdminEditField>
                </>
              ) : (
                <button
                  onClick={() => { const url = ctaSecondaryUrl || "#spaces"; if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); } else if (url.startsWith("#")) { const el = document.querySelector(url); if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { window.location.href = `/${url}`; } } else { window.location.href = url; } }}
                  className="bg-white/20 hover:bg-white/30 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors border border-white/40"
                >
                  {ctaSecondary}
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          {stats.length > 0 && (
            <div className="flex flex-wrap justify-center gap-8 mt-10">
              {stats.map((s, i) => (
                <div key={i} className="text-center relative group/stat">
                  {isAdmin && onUpdate && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteStat(i); }}
                      className="absolute -top-3 -right-3 z-20 w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/stat:opacity-100"
                      title="Remove metric"
                    >
                      <i className="ri-close-line" style={{ fontSize: "10px" }}></i>
                    </button>
                  )}
                  {isAdmin && onUpdate ? (
                    <>
                      <AdminEditField
                        value={s.value}
                        onSave={(v) => setStat(i, "value", v)}
                        inputClassName="text-2xl font-extrabold text-green-400"
                      >
                        <div className="text-2xl font-extrabold text-green-400">{s.value}</div>
                      </AdminEditField>
                      <AdminEditField
                        value={s.label}
                        onSave={(v) => setStat(i, "label", v)}
                        inputClassName="text-xs text-white/70 mt-0.5"
                      >
                        <div className="text-xs text-white/70 mt-0.5">{s.label}</div>
                      </AdminEditField>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-extrabold text-green-400">{s.value}</div>
                      <div className="text-xs text-white/70 mt-0.5">{s.label}</div>
                    </>
                  )}
                </div>
              ))}
              {isAdmin && onUpdate && (
                <button
                  onClick={(e) => { e.stopPropagation(); addStat(); }}
                  className="flex flex-col items-center justify-center text-white/40 hover:text-white/70 transition-colors cursor-pointer px-3"
                  title="Add new metric"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-dashed border-white/30 hover:border-white/50">
                    <i className="ri-add-line text-sm"></i>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest font-bold mt-1">Add</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}